/**
 * DocumentComponents — v1.59 checkpoint 14.2.
 *
 * Componenti riutilizzabili per il Riferimento Documento strutturato:
 *  - `DocumentBadge`: chip compatto DS-coerente (short_label · numero)
 *  - `DocumentReferenceFields`: form accordion editabile (tipo, numero,
 *    data, protocollo, note + upload allegato)
 *  - `useDocumentTypes`: hook che carica l'anagrafica una-tantum.
 *
 * Design coerente col resto della Suite (Turchese, spacing stone,
 * rounded-md morbido). Contrasto testo calcolato automaticamente
 * come per le Etichette.
 */
import { useEffect, useMemo, useState } from "react";
import { Paperclip, Upload, X, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listDocumentTypes,
  uploadAttachment,
  linkAttachment,
  unlinkAttachment,
  listMovementAttachments,
  attachmentDownloadUrl,
} from "@/lib/api";


/** Calcolo del contrasto testo su sfondo colore (stesso algoritmo TagList). */
export function contrastText(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#0F172A";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#0F172A" : "#FFFFFF";
}


/** Hook globale che carica una-tantum i tipi documento attivi. */
let _cachedTypes = null;
let _cachedPromise = null;
export function useDocumentTypes(includeInactive = false) {
  const [types, setTypes] = useState(includeInactive ? [] : _cachedTypes || []);
  useEffect(() => {
    if (!includeInactive && _cachedTypes) {
      setTypes(_cachedTypes);
      return;
    }
    if (!includeInactive && !_cachedPromise) {
      _cachedPromise = listDocumentTypes({ includeInactive: false })
        .then((data) => {
          _cachedTypes = data || [];
          return _cachedTypes;
        })
        .catch(() => []);
    }
    (includeInactive
      ? listDocumentTypes({ includeInactive: true })
      : _cachedPromise
    ).then((data) => setTypes(data || []));
  }, [includeInactive]);
  return types;
}

export function invalidateDocumentTypesCache() {
  _cachedTypes = null;
  _cachedPromise = null;
}


/**
 * DocumentBadge — chip morbido con short_label + numero documento.
 * Props:
 *  - type: {short_label, name, color} | null
 *  - number: string | null
 *  - attachedCount: number
 *  - size: 'sm' | 'md'
 *  - onClickAttachment: () => void (opzionale, per aprire preview)
 */
export function DocumentBadge({
  type, number, date, attachedCount = 0, size = "sm",
  onClickAttachment, testId,
}) {
  if (!type && !number && !attachedCount) return null;
  const color = type?.color || "#6B7280";
  const shortLabel = type?.short_label || type?.name?.slice(0, 3) || "DOC";
  const bg = `${color}1a`; // 10% opacity
  const border = `${color}4d`; // 30% opacity
  const dims = size === "md" ? "text-[11px] px-2 py-0.5" : "text-[10px] px-1.5 py-0.5";
  const dotSize = size === "md" ? "h-2.5 w-2.5" : "h-2 w-2";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-medium ${dims}`}
      style={{ background: bg, borderColor: border, color }}
      data-testid={testId || "document-badge"}
      title={`${type?.name || "Documento"}${number ? ` · ${number}` : ""}${date ? ` · ${date}` : ""}`}
    >
      <span className={`rounded-sm ${dotSize}`} style={{ background: color }} />
      <span className="font-semibold uppercase tracking-wide">{shortLabel}</span>
      {number && <span className="font-mono opacity-80">· {number}</span>}
      {attachedCount > 0 && (
        <button
          type="button"
          onClick={onClickAttachment}
          className="ml-0.5 inline-flex items-center hover:opacity-70"
          title={attachedCount === 1 ? "1 allegato" : `${attachedCount} allegati`}
          data-testid={`${testId || "document-badge"}-paperclip`}
        >
          <Paperclip size={size === "md" ? 11 : 9} strokeWidth={2.4} />
          {attachedCount > 1 && (
            <span className="ml-0.5 text-[9px]">+{attachedCount - 1}</span>
          )}
        </button>
      )}
    </span>
  );
}


/**
 * DocumentReferenceFields — form editabile (accordion).
 * Props:
 *  - value: { doc_type_id, doc_number, doc_date, doc_protocol, doc_notes }
 *  - onChange: (nextValue) => void
 *  - entityType/entityId: opzionali; se passati, carica e permette upload allegato
 *  - defaultOpen: boolean
 *  - testIdPrefix: string
 */
export function DocumentReferenceFields({
  value = {},
  onChange,
  entityType,
  entityId,
  defaultOpen = false,
  testIdPrefix = "docref",
}) {
  const types = useDocumentTypes(false);
  const [open, setOpen] = useState(defaultOpen || isPopulated(value));
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (entityType && entityId) {
      listMovementAttachments(entityType, entityId)
        .then((data) => setAttachments(data || []))
        .catch(() => setAttachments([]));
    }
  }, [entityType, entityId]);

  const setField = (k, v) => onChange?.({ ...value, [k]: v });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !entityType || !entityId) return;
    if (file.size > 20 * 1024 * 1024) {
      setError("File troppo grande (massimo 20 MB)");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const att = await uploadAttachment(file);
      await linkAttachment(entityType, entityId, att.id, attachments.length === 0);
      const updated = await listMovementAttachments(entityType, entityId);
      setAttachments(updated || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore nell'upload";
      setError(typeof detail === "string" ? detail : "Errore nell'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveAttachment = async (attId) => {
    try {
      await unlinkAttachment(entityType, entityId, attId);
      const updated = await listMovementAttachments(entityType, entityId);
      setAttachments(updated || []);
    } catch {
      /* silent */
    }
  };

  return (
    <div className="rounded-md border border-stone-200 bg-stone-50/40" data-testid={`${testIdPrefix}-container`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-stone-700 hover:bg-stone-50"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <span className="flex items-center gap-1.5">
          <FileText size={13} strokeWidth={2} className="text-primary" />
          Riferimento documento
          {isPopulated(value) && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold text-primary">
              Compilato
            </span>
          )}
        </span>
        <span className="text-[10px] text-stone-400">{open ? "Nascondi" : "Mostra"}</span>
      </button>
      {open && (
        <div className="border-t border-stone-200 p-3 space-y-3" data-testid={`${testIdPrefix}-panel`}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Tipo</Label>
              <Select
                value={value.doc_type_id || ""}
                onValueChange={(v) => setField("doc_type_id", v || null)}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-type`}>
                  <SelectValue placeholder="Non specificato" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-sm" style={{ background: t.color }} />
                        {t.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Numero</Label>
              <Input
                type="text"
                maxLength={64}
                value={value.doc_number || ""}
                onChange={(e) => setField("doc_number", e.target.value)}
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-number`}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Data</Label>
              <Input
                type="date"
                value={value.doc_date || ""}
                onChange={(e) => setField("doc_date", e.target.value || null)}
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-date`}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Protocollo</Label>
              <Input
                type="text"
                maxLength={64}
                value={value.doc_protocol || ""}
                onChange={(e) => setField("doc_protocol", e.target.value)}
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-protocol`}
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-stone-500">Note</Label>
            <Textarea
              rows={2}
              maxLength={2000}
              value={value.doc_notes || ""}
              onChange={(e) => setField("doc_notes", e.target.value)}
              className="text-xs"
              data-testid={`${testIdPrefix}-notes`}
            />
          </div>
          {entityType && entityId ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">
                  Allegato ({attachments.length})
                </Label>
                <label className="inline-flex items-center gap-1 cursor-pointer rounded border border-stone-200 bg-white px-2 py-0.5 text-[10px] hover:border-primary/40 hover:bg-primary/5">
                  {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                  {uploading ? "Caricamento…" : "Carica"}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.tiff,.webp"
                    onChange={handleFile}
                    className="hidden"
                    disabled={uploading}
                    data-testid={`${testIdPrefix}-upload`}
                  />
                </label>
              </div>
              {error && (
                <div className="rounded bg-rose-50 px-2 py-1 text-[10px] text-rose-700 mb-1" data-testid={`${testIdPrefix}-error`}>
                  {error}
                </div>
              )}
              <div className="space-y-1">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 rounded border border-stone-200 bg-white px-2 py-1 text-[11px]"
                    data-testid={`${testIdPrefix}-attachment-${a.id}`}
                  >
                    <FileText size={12} className="text-stone-400" />
                    <a
                      href={attachmentDownloadUrl(a.id, entityType, entityId)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 truncate text-stone-700 hover:text-primary"
                      title={a.filename}
                    >
                      {a.filename}
                    </a>
                    <span className="text-[9px] text-stone-400">
                      {formatSize(a.size_bytes)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(a.id)}
                      className="text-stone-400 hover:text-rose-600"
                      title="Rimuovi collegamento (il file resta disponibile finché è collegato altrove)"
                      data-testid={`${testIdPrefix}-remove-${a.id}`}
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
                {attachments.length === 0 && (
                  <div className="text-[10px] text-stone-400 italic">Nessun allegato</div>
                )}
              </div>
            </div>
          ) : (
            <div
              className="rounded border border-dashed border-stone-300 bg-stone-50 px-2.5 py-1.5 text-[10px] text-stone-500"
              data-testid={`${testIdPrefix}-upload-hint`}
            >
              <span className="font-medium text-stone-700">Allegati:</span>{" "}
              salva prima il record per poter caricare i file allegati.
              Potrai aggiungere il documento riaprendo il form in modifica.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function isPopulated(v) {
  if (!v) return false;
  return !!(v.doc_type_id || v.doc_number || v.doc_date || v.doc_protocol || v.doc_notes);
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}


/** Preview popover per il badge — read-only view del riferimento. */
export function DocumentPreview({ type, docNumber, docDate, docProtocol, docNotes, attachments = [], entityType, entityId, children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-3 text-xs" align="start">
        <div className="space-y-2">
          {type && (
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: type.color }} />
              <span className="font-semibold">{type.name}</span>
            </div>
          )}
          {docNumber && (
            <div>
              <span className="text-stone-500">Numero: </span>
              <span className="font-mono">{docNumber}</span>
            </div>
          )}
          {docDate && (
            <div>
              <span className="text-stone-500">Data: </span>
              <span>{docDate}</span>
            </div>
          )}
          {docProtocol && (
            <div>
              <span className="text-stone-500">Protocollo: </span>
              <span>{docProtocol}</span>
            </div>
          )}
          {docNotes && (
            <div className="text-stone-600 whitespace-pre-wrap border-t border-stone-100 pt-2 text-[11px]">
              {docNotes}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="border-t border-stone-100 pt-2 space-y-1">
              {attachments.map((a) => (
                <a
                  key={a.id}
                  href={attachmentDownloadUrl(a.id, entityType, entityId)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-primary hover:underline"
                >
                  <Paperclip size={11} /> {a.filename}
                </a>
              ))}
            </div>
          )}
          {!type && !docNumber && !docDate && !docProtocol && !docNotes && attachments.length === 0 && (
            <div className="italic text-stone-400">Nessun riferimento documento</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
